// Selection handle positions & hit-testing (world-space).
import type { CanvasElement, Point } from '@extapia/shared';
import { boundsOf, mergeBounds } from '@extapia/shared';

export type HandleKind =
  | 'nw' | 'n' | 'ne'
  | 'w'  |       'e'
  | 'sw' | 's' | 'se'
  | 'rotate';

export interface Handle {
  kind: HandleKind;
  x: number;
  y: number;
  cursor: string;
}

const CURSORS: Record<HandleKind, string> = {
  nw: 'nwse-resize',
  n:  'ns-resize',
  ne: 'nesw-resize',
  w:  'ew-resize',
  e:  'ew-resize',
  sw: 'nesw-resize',
  s:  'ns-resize',
  se: 'nwse-resize',
  rotate: 'grab',
};

export const handleSize = (zoom: number): number => 8 / zoom;

// Given selection bounds (world space) return handle positions.
export const handlesForSelection = (selected: CanvasElement[]): Handle[] => {
  const merged = mergeBounds(selected.map(boundsOf));
  if (!merged) return [];
  const { x1, y1, x2, y2 } = merged;
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2;
  const rotateY = y1 - 30; // above the bounding box
  return [
    { kind: 'nw', x: x1, y: y1, cursor: CURSORS.nw },
    { kind: 'n',  x: cx, y: y1, cursor: CURSORS.n },
    { kind: 'ne', x: x2, y: y1, cursor: CURSORS.ne },
    { kind: 'w',  x: x1, y: cy, cursor: CURSORS.w },
    { kind: 'e',  x: x2, y: cy, cursor: CURSORS.e },
    { kind: 'sw', x: x1, y: y2, cursor: CURSORS.sw },
    { kind: 's',  x: cx, y: y2, cursor: CURSORS.s },
    { kind: 'se', x: x2, y: y2, cursor: CURSORS.se },
    { kind: 'rotate', x: cx, y: rotateY, cursor: CURSORS.rotate },
  ];
};

export const hitHandle = (p: Point, handles: Handle[], zoom: number): Handle | null => {
  const r = 10 / zoom;
  for (const h of handles) {
    if (Math.abs(h.x - p.x) <= r && Math.abs(h.y - p.y) <= r) return h;
  }
  return null;
};

// Compute resize deltas + new bounds given a handle drag.
export const applyResize = (
  original: { x: number; y: number; width: number; height: number },
  handle: HandleKind,
  dx: number,
  dy: number,
  preserveAspect: boolean,
): { x: number; y: number; width: number; height: number } => {
  let { x, y, width, height } = original;
  const minSize = 2;

  const setW = (w: number) => { width = Math.max(minSize, w); };
  const setH = (h: number) => { height = Math.max(minSize, h); };
  const setLeft = (nx: number) => {
    const right = x + width;
    x = Math.min(right - minSize, nx);
    width = right - x;
  };
  const setTop = (ny: number) => {
    const bot = y + height;
    y = Math.min(bot - minSize, ny);
    height = bot - y;
  };

  switch (handle) {
    case 'e':  setW(original.width + dx); break;
    case 'w':  setLeft(original.x + dx); break;
    case 's':  setH(original.height + dy); break;
    case 'n':  setTop(original.y + dy); break;
    case 'ne': setW(original.width + dx); setTop(original.y + dy); break;
    case 'nw': setLeft(original.x + dx); setTop(original.y + dy); break;
    case 'se': setW(original.width + dx); setH(original.height + dy); break;
    case 'sw': setLeft(original.x + dx); setH(original.height + dy); break;
    default: break;
  }

  if (preserveAspect && (handle === 'ne' || handle === 'nw' || handle === 'se' || handle === 'sw')) {
    const aspect = original.width / Math.max(1, original.height);
    if (width / height > aspect) width = height * aspect;
    else height = width / aspect;
  }
  return { x, y, width, height };
};
